import { UnitChild, UnitOption } from '@/types/performance/MeasureIndicators';
import { unitChildSDK } from '@/types/sdk/model';

/**
 * Helper functions for unit-related operations in MeasureIndicators
 */

export interface UnitHelpers {
  getUnitChildren: (unitId: string) => UnitChild[];
  isThangDoUnit: (unitId: string) => boolean;
  getChildUnitLabel: (unitId: string, childId: string) => string;
  getChildUnitLabelFromParent: (parentID: any, childValue: string, units: any) => string;
  ensureChildUnitsInList: (unitId: string, listdonvicon: unitChildSDK[]) => unitChildSDK[];
  transformUnitsToOptions: () => UnitOption[];
  getChildUnitValue: (unitId: string, childId: string) => string;
  getUnitLabel: (unitId: string) => string;
  getUnitHinhThucTinh: (unitId: string) => number;
}

/**
 * Get child units for a given unit ID
 */
export const getUnitChildren = (unitId: string, units: any): UnitChild[] => {
  if (!units) return [];

  const unitsData = Array.isArray(units) ? units : Object.values(units);
  const unit = unitsData.find((u: any) => String(u.ID || u.id) === String(unitId));

  if (unit) {
    const childs = unit.Childs ?? unit.childs ?? [];
    if (Array.isArray(childs)) {
      return childs.map((child: any) => ({
        label: child.Ten || child.ten,
        value: String(child.giaTri ?? child.GiaTri ?? null)
      }));
    }
  }

  return [];
};

/**
 * Check if a unit is "thang đo" type (has children)
 */
export const isThangDoUnit = (unitId: string, units: any): boolean => {
  if (!units) return false;

  const unitsData = Array.isArray(units) ? units : Object.values(units);
  const unit = unitsData.find((u: any) => String(u.ID || u.id) === String(unitId));

  if (unit) {
    const childs = unit.Childs ?? unit.childs ?? [];
    return Array.isArray(childs) && childs.length > 0;
  }

  return false;
};

export const getChildUnitValue = (unitId: string, childId: string, units: any): string => {
  if (!isThangDoUnit(unitId, units)) return childId;

  const unitsData = Array.isArray(units) ? units : Object.values(units);
  const unit = unitsData.find((u: any) => String(u.ID || u.id) === String(unitId));

  if (unit) {
    const childs = unit.Childs ?? unit.childs ?? [];
    if (Array.isArray(childs)) {
      const foundChild = childs.find((child: any) => String(child.giaTri ?? child.GiaTri) === String(childId));
      if (foundChild) {
        return String(foundChild.giaTri ?? foundChild.GiaTri ?? foundChild.id ?? foundChild.ID ?? childId);
      }
      return childs.length > 0 ? String(childs[0].giaTri ?? childs[0].GiaTri ?? childs[0].id ?? childs[0].ID ?? childId) : childId;
    }
  }

  return childId;
};
/** `true` / xuôi = xuôi; `false` / `nguoc` = ngược (đồng bộ BE). */
export const isLoaiKPINguoc = (loaiKPI?: boolean | string | number | null): boolean => {
  if (loaiKPI === false || loaiKPI === 0) return true;
  if (typeof loaiKPI === 'string') {
    const s = loaiKPI.trim().toLowerCase();
    return s === 'nguoc' || s === 'false' || s === '0';
  }
  return false;
};

export const getUnitChildsArray = (parentID: any, units: any): any[] => {
  if (!parentID || !units) return [];
  const unitsData = Array.isArray(units) ? units : Object.values(units);
  const unit = unitsData.find((u: any) => String(u.ID || u.id) === String(parentID));
  const childs = unit?.Childs ?? unit?.childs ?? [];
  return Array.isArray(childs) ? childs : [];
};

/**
 * Quy đổi giá trị thực tế → tên đơn vị thang đo (đồng bộ BE QuyDoiThucTeThanhTenDonVi).
 */
export const quyDoiThucTeThanhTenDonVi = (
  thucTeValue: number | string,
  loaiKPI: boolean | string | undefined | null,
  listDonViCon: any[]
): string => {
  if (!listDonViCon?.length) {
    const num = typeof thucTeValue === 'number' ? thucTeValue : parseFloat(String(thucTeValue));
    if (Number.isNaN(num)) return String(thucTeValue ?? '');
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  const strVal = String(thucTeValue ?? '').trim();
  if (strVal === '') return '';

  const byName = listDonViCon.find(
    (child: any) => String(child.Ten ?? child.ten ?? '').trim().toLowerCase() === strVal.toLowerCase()
  );
  if (byName) return byName.Ten || byName.ten || strVal;

  const exact = listDonViCon.find(
    (child: any) => String(child.giaTri ?? child.GiaTri) === strVal
  );
  if (exact) return exact.Ten || exact.ten || strVal;

  let numValue = typeof thucTeValue === 'number' ? thucTeValue : parseFloat(strVal);
  if (Number.isNaN(numValue)) return strVal;

  const normalized = listDonViCon
    .map((child: any) => ({
      giaTri: Number(child.giaTri ?? child.GiaTri),
      ten: String(child.Ten ?? child.ten ?? ''),
    }))
    .filter((c) => !Number.isNaN(c.giaTri));

  if (!normalized.length) return strVal;
  if (isLoaiKPINguoc(loaiKPI)) {
    numValue = 100 - numValue;
    const candidates = normalized.filter((c) => c.giaTri >= numValue);
    candidates.sort((a, b) => {
      const aExact = a.giaTri === numValue ? 1 : 0;
      const bExact = b.giaTri === numValue ? 1 : 0;
      if (bExact !== aExact) return bExact - aExact;
      return a.giaTri - b.giaTri;
    });
    return candidates[0]?.ten ?? normalized[0].ten;
  }

  const candidates = normalized.filter((c) => c.giaTri <= numValue);
  candidates.sort((a, b) => {
    const aExact = a.giaTri === numValue ? 1 : 0;
    const bExact = b.giaTri === numValue ? 1 : 0;
    if (bExact !== aExact) return bExact - aExact;
    return b.giaTri - a.giaTri;
  });
  return candidates[0]?.ten ?? normalized[0].ten;
};

/**
 * Get the label for a given child unit by giaTri from parent unit object
 * This is an optimized version that accepts parent unit directly
 */
export const getChildUnitLabelFromParent = (parentID: any, childValue: string, units: any): string => {
  if (!parentID) return String(childValue ?? '');

  const childs = getUnitChildsArray(parentID, units);
  if (childs.length) {
    const foundChild = childs.find((child: any) => String(child.giaTri ?? child.GiaTri) === String(childValue));
    return foundChild ? foundChild.Ten || foundChild.ten || String(childValue ?? '') : String(childValue ?? '');
  }
  return String(childValue ?? '');
};

/**
 * Get the label for a given child unit by giaTri
 */
export const getChildUnitLabel = (unitId: string, childId: string, units: any): string => {
  if (!isThangDoUnit(unitId, units)) return childId;

  const childs = getUnitChildsArray(unitId, units);
  if (childs.length) {
    const foundChild = childs.find((child: any) => String(child.giaTri ?? child.GiaTri) === String(childId));
    if (foundChild) {
      return foundChild.Ten || foundChild.ten || String(childId);
    }
  }

  return '';
};

/**
 * Ensure child units are added to listdonvicon if missing
 */
export const ensureChildUnitsInList = (
  unitId: string,
  listdonvicon: unitChildSDK[],
  tenantID: string,
  units: any
): unitChildSDK[] => {
  if (!isThangDoUnit(unitId, units)) return listdonvicon;

  const unitsData = Array.isArray(units) ? units : Object.values(units);
  const unit = unitsData.find((u: any) => String(u.ID || u.id) === String(unitId));

  if (unit) {
    const childs = unit.Childs ?? unit.childs ?? [];
    if (Array.isArray(childs)) {
      // Create new list with all child units using giaTri
      const newList: unitChildSDK[] = childs.map((child: any) => ({
        id: child.id || child.ID || '',
        ten: child.Ten || child.ten || '',
        tenNgan: child.tenNgan || child.TenNgan || '',
        giaTri: child.giaTri ?? child.GiaTri ?? 0,
        idDoLuong: unitId,
        site: tenantID || ''
      }));

      return newList;
    }
  }

  return listdonvicon;
};

/**
 * Transform units data to options format for Select components
 */
export const transformUnitsToOptions = (units: any): UnitOption[] => {
  if (!units) return [];

  const unitsData = Array.isArray(units) ? units : Object.values(units);

  return unitsData.map((unit: any) => ({
    label: unit.Ten || unit.ten || '',
    value: String(unit.ID || unit.id || '')
  }));
};

/**
 * Get the label for a given unit ID
 */
export const getUnitLabel = (unitId: string, units: any): string => {
  if (!unitId || !units) return '';
  const unitsData = Array.isArray(units) ? units : Object.values(units);
  const unit = unitsData.find((u: any) => String(u.ID || u.id) === String(unitId));
  return unit ? String(unit.Ten || unit.ten || '').trim() : '';
};

/** Hình thức tính mặc định theo đơn vị đo. Thang đo luôn Trung bình (2). */
export const getUnitHinhThucTinh = (unitId: string, units: any): number => {
  if (!unitId || !units) return 1;
  if (isThangDoUnit(unitId, units)) return 2;

  const unitsData = Array.isArray(units) ? units : Object.values(units);
  const unit = unitsData.find((u: any) => String(u.ID || u.id) === String(unitId));
  const raw = unit?.HinhThucTinh ?? unit?.hinhThucTinh;
  const parsed = Number(raw);
  return parsed === 2 || parsed === 3 ? parsed : 1;
};

/** idDoLuong từ định nghĩa KPI; không có thì lấy value đơn vị đầu tiên trong danh sách. */
export const resolveKpiDefinitionIdDoLuong = (
  row: { idDoLuong?: string | null },
  unitHelpers: Pick<UnitHelpers, 'transformUnitsToOptions'>
): string => {
  const fromRow = row.idDoLuong?.trim() ?? '';
  if (fromRow) return fromRow;
  return unitHelpers.transformUnitsToOptions()[0]?.value?.trim() ?? '';
};

/**
 * Create unit helpers object with all functions
 */
export const createUnitHelpers = (units: any, tenantID: string): UnitHelpers => ({
  getUnitChildren: (unitId: string) => getUnitChildren(unitId, units),
  isThangDoUnit: (unitId: string) => isThangDoUnit(unitId, units),
  getChildUnitLabel: (unitId: string, childId: string) => getChildUnitLabel(unitId, childId, units),
  getChildUnitLabelFromParent: (parentID: any, childValue: string, unitsArg: any) =>
    getChildUnitLabelFromParent(parentID, childValue, unitsArg ?? units),
  ensureChildUnitsInList: (unitId: string, listdonvicon: unitChildSDK[]) =>
    ensureChildUnitsInList(unitId, listdonvicon, tenantID, units),
  transformUnitsToOptions: () => transformUnitsToOptions(units),
  getChildUnitValue: (unitId: string, childId: string) => getChildUnitValue(unitId, childId, units),
  getUnitLabel: (unitId: string) => getUnitLabel(unitId, units),
  getUnitHinhThucTinh: (unitId: string) => getUnitHinhThucTinh(unitId, units),
});
